import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Switch, Typography, Alert, message, Skeleton } from 'antd';
import { ToolOutlined } from '@ant-design/icons';
import { getUserSettings, updateMaintenance, clearMessages } from '../../features/settings/settingsSlice';

const { Title, Text } = Typography;

const MaintenanceToggle = () => {
  const dispatch = useDispatch();
  const { maintenance, loading, error, success } = useSelector((state) => state.settings);

  // Fetch settings on component mount
  useEffect(() => {
    dispatch(getUserSettings());
  }, [dispatch]);

  // Handle success/error messages
  useEffect(() => {
    if (success) {
      message.success(success);
      dispatch(clearMessages());
    }
    if (error) {
      message.error(error);
      dispatch(clearMessages());
    }
  }, [success, error, dispatch]);

  // Clear messages on unmount
  useEffect(() => {
    return () => {
      dispatch(clearMessages());
    };
  }, [dispatch]);

  const handleMaintenanceToggle = (checked) => {
    dispatch(updateMaintenance(checked));
  };

  if (loading && maintenance === false && !error) {
    return (
      <Card className="shadow-soft border border-border-light rounded-xl">
        <Skeleton active />
      </Card>
    );
  }

  return (
    <Card className="shadow-soft border border-border-light rounded-xl">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <ToolOutlined className="text-xl text-orange-600" />
          <Title level={3} className="mb-0">
            Maintenance Mode
          </Title>
        </div>

        {/* Description */}
        <Alert
          message="Maintenance Mode Control"
          description="When enabled, maintenance mode will display a maintenance page to visitors while you work on your site."
          type="info"
          showIcon
          className="mb-4"
        />

        {/* Toggle Switch */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <Text strong className="block mb-1">
              Maintenance Mode
            </Text>
            <Text type="secondary" className="text-sm">
              {maintenance 
                ? "Your site is currently in maintenance mode. Visitors will see a maintenance page." 
                : "Your site is live and accessible to visitors."
              }
            </Text>
          </div>
          <div className="ml-4">
            <Switch
              checked={maintenance}
              onChange={handleMaintenanceToggle}
              loading={loading}
              checkedChildren="ON"
              unCheckedChildren="OFF"
              size="default"
            />
          </div>
        </div>

        {/* Status Alert */}
        {maintenance ? (
          <Alert
            message="Maintenance Mode Active"
            description="Your site is currently in maintenance mode. Remember to turn it off when you're done with maintenance work."
            type="warning"
            showIcon
          />
        ) : (
          <Alert
            message="Site is Live"
            description="Your site is currently accessible to all visitors."
            type="success"
            showIcon
          />
        )}
      </div>
    </Card>
  );
};

export default MaintenanceToggle;